//Measure.tsx ==============================================================
import "../Util/Prototypes";

import * as Default from "../Default";
import * as DrawerMenu from "../Menus/DrawerMenu";
import * as GraLay from "../Util/GraLay";
import * as MainMap from "../MainMap";
import * as Settings from "../Settings";
import * as Util from "../Util/Util";
import * as Util_App from "../Util/Util_App";
import * as Util_MUI from "../Util/Util_MUI";
import * as WidgetHeading from "../Widgets/WidgetHeading";

import { Button, ButtonGroup, Checkbox, FormControlLabel, Icon, Paper, TextField, Typography } from "@mui/material";

import { blueGrey, yellow } from '@mui/material/colors';
import { ButtonProps } from "@mui/material/Button";

import React from "react";

//ESRI javascript API.
import Color from "@arcgis/core/Color";
import FeatureLayer from "@arcgis/core/layers/FeatureLayer";
import FeatureSnappingLayerSource from "@arcgis/core/views/interactive/snapping/FeatureSnappingLayerSource";
import Field from "@arcgis/core/layers/support/Field";
import * as geometryEngine from "@arcgis/core/geometry/geometryEngine";
import Graphic from "@arcgis/core/Graphic";
import LabelClass from "@arcgis/core/layers/support/LabelClass";
import Point from "@arcgis/core/geometry/Point";
import Polyline from "@arcgis/core/geometry/Polyline";
import SimpleLineSymbol from "@arcgis/core/symbols/SimpleLineSymbol";
import SimpleRenderer from "@arcgis/core/renderers/SimpleRenderer.js";

interface Graphic_extended extends __esri.Graphic {
   myId: string;
}

const toolName = "Measure";

type toolType =  "point" | "multipoint" | "polyline" | "polygon" | "rectangle" | "circle" | "mesh";

let drawingTool: "point" | "multipoint" | "polyline" | "polygon" | "rectangle" | "circle" | "mesh" = "polyline"; // polyline, polygon, rectangle, circle, or "".
let activeTool:  "point" | "multipoint" | "polyline" | "polygon" | "rectangle" | "circle" | "mesh" = drawingTool; // polyline, polygon, rectangle, circle, or "".

let drawingMode: "hybrid" | "freehand" | "click" = "click";       // click or hybrid. hybrid is freehand.
let activeGra: Graphic_extended = null;   // Active graphic being drawn or updated.

let prevPnt: __esri.Point;  // Previous mouse click point.
let curPnt: __esri.Point;   // Current mouse move point.
let prevPrevPnt: __esri.Point;

let updateTool: "move" | "transform" | "reshape";       // reshape, transform, or move.

let drawingState: "active" | "start" | "complete" | "cancel" = null; // start, active, complete or cancel or "".
let updateState:  "active" | "start" | "complete" = null; // start, active, complete, cancel, or "".

const minScreenDistForLabel = 30;   //pixels.
const minScreenAreaForLabel = 3000; //pixels.

let sketchVM: __esri.SketchViewModel; // API sketch view model.
let view2D: boolean;
let view3D: boolean;
let freehand = false; // True means hybrid drawing mode.
let labelMeasurements = true; //True means label lengths and areas.
let hideResultsPanel = false;
let incompleteLabelsId: string = ""; //ID of in progress labels after measure was exited.

let lineFL: __esri.FeatureLayer;  // Line feature layer for client-side labeling last segment.
let lineGra: Graphic_extended;      // Line graphic for client-side labeling.

let segmentLabels: Graphic_extended[]; //Array of graphic points for segment text symbol lengths.
let areaLabel: Graphic_extended;       // Graphic for area text symbol.
let distanceLabel: Graphic_extended;   //Graphic for line text symbol.
let circleRadiusLine: Graphic_extended;

let click_eventHandler: IHandle;

let helpMsg: string;

// Left panel measurement results.
let bearing: string;
let distance: number;
let perimeter: number;
let area: number;

let segmentText: string;
let distanceText: string;
let perimeterText: string;
let areaText: string;

let segmentVisible: boolean;
let bearingVisible: boolean;
let distanceVisible: boolean;
let radiusVisible: boolean;
let perimeterVisible: boolean;
let areaVisible: boolean;

//Dropdown menu items.
const distUnitNames = ["Auto", "Feet", "Kilometers", "Meters", "Miles"];
let distUnitValue: string = distUnitNames[0];
let distUnitText: string = "";
let distUnitLabel: string = "";
let distUnitAPI: __esri.LinearUnits;

const areaUnitNames = ["Auto", "Acres", "Square feet", "Square kilometers", "Square meters", "Square miles"];
let areaUnitValue: string = areaUnitNames[0];
let areaUnitText: string = "";
let areaUnitLabel: string = "";
let areaUnitAPI: __esri.AreaUnits;

const segmentSymbol = {
   type: "text",
   angle: 0,
   color: "black",
   font: {
      size: 10,
      weight: "bold",
   },
   haloColor: "white",
   haloSize: 2,
   text: "",
   verticalAlignment: "middle",
};

const totalSymbol = {
   type: "text",
   angle: 0,
   color: "black",
   font: {
      size: 11,
      weight: "bold",
   },
   haloColor: "white",
   haloSize: 2,
   text: "",
   verticalAlignment: "middle",
};

//Clear this tool.
export function clearTool() {
}

interface iProps {
}

//Render function.
export function Render(props: iProps) {
   //Util.logMsg("Top of SelectOnMap Render");

   //Wire up component for each time render is called, but not on refresh.
   const [initComponent, setState] = React.useState(() => {
      view2D = MainMap.view2D;
      view3D = !view2D;
   });

   //Set refresh method.
   const [ignored, refresh] = React.useReducer((x: number) => x + 1, 0);

   //Component did mount.
   React.useEffect(() => {
      //Load the sketch view model module.
      import("@arcgis/core/widgets/Sketch/SketchViewModel").then(({ default: SketchViewModel }) => {

         const sketchSource = new FeatureSnappingLayerSource({ layer: GraLay.sketch });
         const measureSource = new FeatureSnappingLayerSource({ layer: GraLay.measure });
         let featureSources = [sketchSource, measureSource]; //Initialize.

         for (const layerName of Settings.sketchSnappingLayers) {
            const snappingLayer = Util_App.getOperationalLayerById(layerName) as __esri.FeatureLayer;
            if (snappingLayer) {
               const featureLayerSource = new FeatureSnappingLayerSource({ layer: snappingLayer });
               featureSources.Add(featureLayerSource);
            }
         }

         //Create feature layer for labeling.
         createLabelFeatureLayer();

         //Create the sketch view model.
         sketchVM = new SketchViewModel({
            layer: GraLay.measure,
            view: MainMap.activeView,
            polylineSymbol: Settings.measureLineSym,
            polygonSymbol: Settings.measurePolygonSym,
            updateOnGraphicClick: true,
            defaultCreateOptions: { mode: drawingMode },
            defaultUpdateOptions: { tool: "reshape", toggleToolOnClick: false, enableRotation: false, enableScaling: true, multipleSelectionEnabled: false },
            snappingOptions: {
               enabled: true,
               featureSources: featureSources,
            },
         });

         //Create events for the sketch view model.
         sketchVM.on("create", function (event) {
            //Util.errMsg(jj + " Event.State: " + event.state, event);
            drawingState = event.state;

            let vertexAddInfo: __esri.VertexAddEventInfo = null;
            let cursorUpdateInfo: __esri.CursorUpdateEventInfo = null;
            let toolType: string = "";

            if (event.toolEventInfo) {
               const toolEventInfo = event.toolEventInfo;

               if (toolEventInfo.type === "vertex-add") {
                  vertexAddInfo = toolEventInfo;
                  toolType = vertexAddInfo.type;

               } else {
                  cursorUpdateInfo = toolEventInfo;
                  toolType = cursorUpdateInfo.type;
               }
            }

            let x: number;
            let y: number;

            // First point. --------------------------------------------------------------
            if (drawingState === "start" && view2D) {
               activeGra = null;
               activeTool = event.tool;
               updateTool = null;

               initLabelGraphics();

               //Save the first click point for all drawing types. (Including rectangles and circles)
               x = vertexAddInfo.vertices[0].coordinates[0];
               y = vertexAddInfo.vertices[0].coordinates[1];

               prevPnt = new Point({ x: x, y: y, spatialReference: MainMap.activeView.spatialReference });
               curPnt = prevPnt;
               prevPrevPnt = prevPnt;

               // Measure in progress. (mouse move) ------------------------------------------
            } else if (drawingState === "active" && view2D) {
               activeGra = event.graphic as Graphic_extended;

               //Set the id and drawing tool name on the active graphic.
               if (activeGra) {
                  if (!activeGra.attributes) {
                     activeGra.attributes = { myId: Util.uid(), drawingTool: drawingTool };
                     incompleteLabelsId = activeGra.attributes.myId as string;
                  }
               }

               //Save mouse intermediate click point.
               if (toolType === "vertex-add") {
                  x = vertexAddInfo.vertices[0].coordinates[0];
                  y = vertexAddInfo.vertices[0].coordinates[1];

                  prevPrevPnt = prevPnt;
                  prevPnt = new Point({ x: x, y: y, spatialReference: MainMap.activeView.spatialReference });

                  //Save mouse move point.
               } else if (toolType === "cursor-update") {
                  x = cursorUpdateInfo.coordinates[0];
                  y = cursorUpdateInfo.coordinates[1];
                  curPnt = new Point({ x: x, y: y, spatialReference: MainMap.activeView.spatialReference });

                  //Add labels.
                  computeMeasurement();
                  buildLabels();
               }

               // Double click or second point on rectangle/circle. ----------------------
            } else if (drawingState === "complete") {

               //Handle freehand rectangle and circle.
               if (!activeGra) {
                  activeGra = event.graphic as Graphic_extended;
                  activeGra.attributes = { myId: Util.uid(), drawingTool: drawingTool };
                  const yMid: number = (activeGra.geometry.extent.ymin + activeGra.geometry.extent.ymax) / 2;
                  curPnt = new Point({ x: activeGra.geometry.extent.xmax, y: yMid, spatialReference: MainMap.activeView.spatialReference });
               }

               prevPnt = prevPrevPnt;

               computeMeasurement();
               buildLabels(true);
               updateLastSegment(""); //Clear client-side label.
               disableDrawingTool(); // Uncomment to disable drawing tool after measuring.
               incompleteLabelsId = "";
               //sketchVM.create(drawingTool); // Restart a new sketch.

               // Measure was canceled. --------------------------------------------------
            } else if (drawingState === "cancel") {
               activeGra = null;
               updateLastSegment("");  //Clear client-side label.
            }

            setHelpMessage();
            refresh();
         });

         //Update graphic edit event.
         sketchVM.on("update", function (event) {

            if (DrawerMenu.curToolName !== "Measure") {
               setTimeout(function () { sketchVM.cancel(); }, 100);
               return;
            }

            updateState = event.state;
            updateTool = event.tool;
            drawingState = null;

            if (updateState === "start") { //First time. ---------------------------------------
               activeGra = event.graphics[0] as Graphic_extended;
               activeTool = activeGra.attributes.drawingTool as toolType;
               initLabelGraphics();
               computeMeasurement();
               buildLabels();

               //Change to transform mode instead of reshape.
               if (view2D) {
                  if (activeTool === "rectangle" || activeTool === "circle") {

                     if (updateTool === "reshape") {
                        setTimeout(function () { sketchVM.cancel(); }, 100); //Wait a bit.

                        let preserveAspect: boolean = false;
                        if (activeTool === "circle") {
                           preserveAspect = true;
                        }

                        setTimeout(function () { sketchVM.update(activeGra, { tool: "transform", preserveAspectRatio: preserveAspect }); }, 200); //Wait for map view to load.
                     }
                  }
               }

            } else if (updateState === "active") { //Update in progress. -------------------------------------
               computeMeasurement();
               buildLabels();

            } else if (updateState === "complete") { // ------------------------------------------
            }

            setHelpMessage();
            refresh();
         });

         //Undo graphic edit event.
         sketchVM.on("undo", function (event) {
         });

         //Redo graphic edit event.
         sketchVM.on("redo", function (event) {
         });

         //Delete graphic event.
         //sketchVM.on("delete", function (event) {
         //});

         //Exit edit graphic mode for map click outside the graphic.
         click_eventHandler = MainMap.activeView.on("click", function (event) {
            MainMap.activeView.hitTest(event).then(function (response: __esri.HitTestResult | __esri.SceneViewHitTestResult) {
               let measureFound: boolean = false;

               if (response.results.length > 0) {
                  for (const result of response.results) {
                     const layer = result.layer;
                     if (layer && layer.id) {
                        if (layer.id.StartsWith("measure")) {
                           measureFound = true;
                        }
                     }
                  }
               }

               if (updateState) {
                  if (!measureFound && (updateState === "start" || updateState === "active") && drawingTool) {
                     sketchVM.cancel();
                  }
               }

            }); //end hit test.
         }); //end map click.

         //Fire up the drawing tool.
         if (drawingTool) {
            sketchVM.create(drawingTool);
         }
         setHelpMessage();
         refresh();

         //Component will unmount. Clear this tool.
         return () => {
            sketchVM.cancel();
            sketchVM.destroy();
            click_eventHandler.remove();

            //Remove incomplete segment labels.
            if (incompleteLabelsId) {
               const gras: Graphic_extended[] = [];

               for (const gra_normal of GraLay.measureLabels.graphics) {
                  const gra = gra_normal as Graphic_extended;
                  const myId = gra.myId;

                  if (myId.StartsWith(incompleteLabelsId)) {
                     gras.Add(gra);
                  }
               }
               GraLay.measureLabels.removeMany(gras);
            }

            //Remove measure feature layer.
            const fl = Util_App.getOperationalLayerById("MeasureFL") as __esri.FeatureLayer;
            if (fl) {
               MainMap.map.remove(fl);
            }
         };

      }).catch((error: Error) => {
         console.error('An error occurred while loading the Sketch View Model module:', error);
      });

   }, []); //end component did mount.

   //Close this tool.
   function close() {
      DrawerMenu.closeTool();
   }

   //Initialize label graphics.
   function initLabelGraphics() {
      segmentLabels = [];
      areaLabel = null;
      distanceLabel = null;
      circleRadiusLine = null;
   }

   //Build labels.
   function buildLabels(complete: boolean = false) {
      if (!view2D || !labelMeasurements) {
         return;
      }

      let textSymbol: __esri.TextSymbol;
      let polyline: __esri.Polyline;
      let polygon: __esri.Polygon;
      let circle: __esri.Circle;
      let pnt1: __esri.Point;
      let pnt2: __esri.Point;

      //Get existing labels for edit mode.
      if (updateTool === "reshape" || updateTool === "transform") {
         if (segmentLabels.length === 0) {

            for (const gra_normal of GraLay.measureLabels.graphics) {
               const gra = gra_normal as Graphic_extended;

               const id = gra.myId;

               if (id.StartsWith(activeGra.attributes.myId) && id.Contains("_")) {
                  if (id.EndsWith("_Area")) {
                     areaLabel = gra;

                  } else if (id.EndsWith("_Dist")) {
                     distanceLabel = gra;

                  } else if (id.EndsWith("_Rad")) {
                     circleRadiusLine = gra;

                  } else {
                     segmentLabels.Add(gra);
                  }
               }
            }
         }
      }

      //Compute number of segments to label if needed.
      let numSeg: number = 0;
      let path: number[][];

      if (activeTool === "polyline") {
         polyline = activeGra.geometry as __esri.Polyline;
         path = polyline.paths[0];
         numSeg = path.length - 1;

         //Label the total distance.
         if (path.length > 1) {

            //Compute the label origin point.
            pnt1 = new Point({ x: path[0][0], y: path[0][1], spatialReference: MainMap.activeView.spatialReference });
            pnt2 = new Point({ x: path[1][0], y: path[1][1], spatialReference: MainMap.activeView.spatialReference });

            let xOffset: number = -25;
            let yOffset: number = 25;

            if (pnt2.x < pnt1.x) {
               xOffset = 25;
            }

            if (pnt2.y > pnt1.y) {
               yOffset = -25;
            }

            xOffset *= MainMap.activeView.resolution;
            yOffset *= MainMap.activeView.resolution;

            const begPnt = new Point({ x: path[0][0] + xOffset, y: path[0][1] + yOffset, spatialReference: MainMap.activeView.spatialReference });

            //Clear the distance label if 3D.
            if (view3D && distanceLabel) {
               GraLay.measureLabels.remove(distanceLabel);
               distanceLabel = null;
            }

            if (!distanceLabel) {
               textSymbol = JSON.parse(JSON.stringify(totalSymbol)) as __esri.TextSymbol; //Clone it.
               distanceLabel = new Graphic({ geometry: begPnt, symbol: textSymbol }) as Graphic_extended;
               distanceLabel.myId = activeGra.attributes.myId + "_Dist";

               if (view2D) {
                  GraLay.measureLabels.add(distanceLabel);
               }
            }

            distanceLabel.geometry = begPnt;

            let distanceLabelSymbol = distanceLabel.symbol as __esri.TextSymbol;
            distanceLabelSymbol.text = "Total: " + distanceText + " " + distUnitLabel;
            distanceLabel.symbol = distanceLabelSymbol;

            //Add the distance label back in for 3D.
            if (view3D) {
               GraLay.measureLabels.add(distanceLabel);
            }
         }

      } else if (activeTool === "circle" && view2D) {
         numSeg = 1;
         circle = activeGra.geometry as __esri.Circle;

         const yMid: number = (circle.extent.ymin + circle.extent.ymax) / 2;
         path = [[circle.centroid.x, circle.centroid.y], [circle.extent.xmax, yMid]];

         //Add the radius line.
         if (!circleRadiusLine) {
            polyline = new Polyline({
               paths: [[[1, 1], [2, 2]]], hasZ: false, hasM: false,
               spatialReference: MainMap.activeView.spatialReference,
            });

            circleRadiusLine = new Graphic({ geometry: polyline, symbol: Settings.measureLineSym }) as Graphic_extended;
            circleRadiusLine.myId = activeGra.attributes.myId + "_Rad";

            if (view2D) {
               GraLay.measureLabels.add(circleRadiusLine);
            }
         }

      } else { //Polygon and rectangle.
         polygon = activeGra.geometry as __esri.Polygon;
         numSeg = polygon.rings[0].length - 1;
         path = polygon.rings[0];

         if (numSeg === 2) {
            numSeg = 1; //Only two points, so just show the segment label for the polygon.
         }

         //Label the area.
         if (path.length > 3) {

            //Clear the area label if 3D.
            if (view3D && areaLabel) {
               GraLay.measureLabels.remove(areaLabel);
               areaLabel = null;
            }

            //Create empty point graphic for the text symbol.
            if (!areaLabel) {
               textSymbol = JSON.parse(JSON.stringify(totalSymbol)) as __esri.TextSymbol; //Clone it.
               textSymbol.verticalAlignment = "bottom";
               areaLabel = new Graphic({ geometry: new Point({ x: 1, y: 1, spatialReference: MainMap.activeView.spatialReference }), symbol: textSymbol }) as Graphic_extended;
               areaLabel.myId = activeGra.attributes.myId + "_Area";

               if (view2D) {
                  GraLay.measureLabels.add(areaLabel);
               }
            }

            //Work around bug when height or width of activeGra is zero.
            let areaLabelSymbol = areaLabel.symbol as __esri.TextSymbol;

            if (areaText === "") {
               areaLabelSymbol.text = "";

            } else {
               const cenPnt: __esri.Point = Util_App.getPointInsidePolygon(polygon);
               areaLabel.geometry = cenPnt;

               //Compute area of active graphic in screen units.
               const pntLL = new Point({ x: polygon.extent.xmin, y: polygon.extent.ymin, spatialReference: MainMap.activeView.spatialReference });
               const pntUR = new Point({ x: polygon.extent.xmax, y: polygon.extent.ymax, spatialReference: MainMap.activeView.spatialReference });
               const screenLL = MainMap.activeView.toScreen(pntLL);
               const screenUR = MainMap.activeView.toScreen(pntUR);
               const screenArea: number = Math.abs(screenUR.x - screenLL.x) * Math.abs(screenUR.y - screenLL.y);
               areaLabelSymbol.text = "";

               if (screenArea > minScreenAreaForLabel) {
                  areaLabelSymbol.text = areaText + " " + areaUnitLabel;

                  //Add the area label back in for 3D.
                  if (view3D) {
                     GraLay.measureLabels.add(areaLabel);
                  }
               }
            }
         }
      }

      //Add missing dimension segments.
      if (segmentLabels.length < numSeg) {
         for (let i: number = segmentLabels.length; i < numSeg; i++) {
            textSymbol = JSON.parse(JSON.stringify(segmentSymbol)) as __esri.TextSymbol; //Clone it.
            const pntGra = new Graphic({ geometry: curPnt, symbol: textSymbol }) as Graphic_extended;
            pntGra.myId = activeGra.attributes.myId + "_" + i;
            segmentLabels.Add(pntGra);

            if (view2D) {
               GraLay.measureLabels.add(pntGra);
            }
         }
      }

      //Update all segment labels.
      for (let i: number = 0; i < numSeg; i++) {
         pnt1 = new Point({ x: path[i][0], y: path[i][1], spatialReference: MainMap.activeView.spatialReference });
         pnt2 = new Point({ x: path[i + 1][0], y: path[i + 1][1], spatialReference: MainMap.activeView.spatialReference });

         const segLabel = getSegmentLabel(pnt1, pnt2);

         //Update last client-side labeling segment.
         if (!complete && updateState !== "start" && updateState !== "active" && isLastSegment(i, numSeg)) {
            segmentLabels[i].geometry = new Point({ x: 1, y: 1, spatialReference: MainMap.activeView.spatialReference }); //Hide the point text graphic.
            //updateLastSegment(segLabel.symbol.text, pnt1, pnt2); //4.19 or greater only. Show the client-side label.

         } else { //Label with point graphic text.
            const point1 = segLabel.geometry as __esri.Point;
            const point2 = segmentLabels[i].geometry as __esri.Point;

            if (point1.x !== point2.x || point1.y !== point2.y) {

               const screenXY1 = MainMap.activeView.toScreen(pnt1);
               const screenXY2 = MainMap.activeView.toScreen(pnt2);
               const screenDist: number = Util_App.twoPntDistance(screenXY1, screenXY2);

               if (screenDist > minScreenDistForLabel) {
                  segmentLabels[i].symbol = segLabel.symbol;
                  segmentLabels[i].geometry = segLabel.geometry;

               } else {
                  segmentLabels[i].geometry = new Point({ x: 1, y: 1, spatialReference: MainMap.activeView.spatialReference }); //Dont show label on short segments.
               }
            }
         }

         if (activeTool === "circle" && view2D) {
            polyline = new Polyline({
               paths: [[[pnt1.x, pnt1.y], [pnt2.x, pnt2.y]]], hasZ: false, hasM: false,
               spatialReference: MainMap.activeView.spatialReference,
            });
            circleRadiusLine.geometry = polyline;
         }
      }

   } //End function.

   //Determine if last labeling segment.
   function isLastSegment(ii: number, numSeg: number): boolean {
      if (activeTool === "polyline" || activeTool === "polygon" || activeTool === "circle") {
         if (activeTool === "polygon") {
            if (numSeg === 1 || ii === (numSeg - 2)) {
               return true;
            }
         } else {
            if (ii === (numSeg - 1)) {
               return true;
            }
         }
      }
      return false;
   }

   //Update last segment label.
   function updateLastSegment(text: string, pnt1?: __esri.Point, pnt2?: __esri.Point) {
      const geom = new Polyline({
         paths: [[[1, 1], [2, 2]]], hasZ: false, hasM: false,
         spatialReference: MainMap.activeView.spatialReference,
      });

      if (text !== "") {
         geom.paths = [[[pnt1.x, pnt1.y], [pnt2.x, pnt2.y]]];
      }

      lineGra.geometry = geom;
      lineGra.attributes.value = text;

      lineFL.applyEdits({ updateFeatures: [lineGra] }).then(function (results: { updateFeatureResults: __esri.FeatureEditResult[] }) {
         if (results.updateFeatureResults.length > 0) {
            if (results.updateFeatureResults[0].error) {
               Util.errMsg("Error", results.updateFeatureResults[0].error);
            }
         }
      });
   }

   //Get segment label.
   function getSegmentLabel(pnt1: __esri.Point, pnt2: __esri.Point): Graphic_extended {

      //Compute mid-segment location.
      const midpoint = new Point({ x: (pnt1.x + pnt2.x) / 2, y: (pnt1.y + pnt2.y) / 2, spatialReference: MainMap.activeView.spatialReference });

      //Compute angle.
      let angle: number = Math.atan2((pnt2.x - pnt1.x), (pnt2.y - pnt1.y)) / Math.PI * 180 - 90;
      if (angle <= -90) {
         angle += 180;
      }

      //Compute offsets.
      let dx: number = 0;
      const dy: number = 7;

      if (angle < -1 && angle > -89.99) {
         dx = Util.parseInteger(((angle / 90) * 7).toString());
      }

      if (angle > 1 && angle <= 90) {
         dx = Util.parseInteger(((angle / 90) * 7).toString());
      }

      //Set output graphic.
      const textSymbol = JSON.parse(JSON.stringify(segmentSymbol)) as __esri.TextSymbol; //Clone it.

      const segDist: number = computeSegmentDistance(pnt1, pnt2);
      const segText = Util.formatTextValue(segDist, "Comma", true);

      textSymbol.text = segText;

      if (activeTool === "circle") {
         textSymbol.text = textSymbol.text + " " + distUnitLabel;
      }

      textSymbol.angle = angle;
      textSymbol.xoffset = dx;
      textSymbol.yoffset = dy;
      textSymbol.font.size = 10;

      const outGra = new Graphic({ geometry: midpoint, symbol: textSymbol }) as Graphic_extended;
      return outGra;
   }

   //Drawing tool changed.
   function drawingToolChanged(inDrawingTool: string) {
      let i: number;
      let j: number = 0;

      if (inDrawingTool === "clear") {
         clearSelected();

      } else if (inDrawingTool === "done") {
         if (drawingTool === "polyline") {
            const line = activeGra.geometry as __esri.Polyline;
            const path = line.paths[0];
            line.removePoint(0, path.length - 1);

         } else { //Polygon.
            const poly = activeGra.geometry as __esri.Polygon;
            const ring = poly.rings[0];

            i = -1;
            for (const pnt of ring) {
               i++;
               if (pnt[0] === curPnt.x && pnt[1] === curPnt.y) {
                  j = i;
               }
            }
            poly.removePoint(0, j);
            segmentLabels[segmentLabels.length - 1].geometry = new Point({ x: 1, y: 1, spatialReference: MainMap.activeView.spatialReference }); //Hide the label for deleted segment.
         }

         curPnt = prevPnt;
         sketchVM.complete();
         return;

      } else { //Drawing tool was clicked.
         if (drawingState === "active") {
            clearSelected();
         }

         if (inDrawingTool === drawingTool) {
            disableDrawingTool();
            hideResultsPanel = true;
            refresh();
            return;
         }

         drawingTool = inDrawingTool as toolType;
         activeTool = drawingTool;

         sketchVM.create(drawingTool);
      }

      drawingState = null;

      segmentText = "";
      bearing = "";
      distanceText = "";
      perimeterText = "";
      areaText = "";

      distUnitText = "";
      areaUnitText = "";

      setHelpMessage();
      refresh();
   }

   //Disable drawing tool.
   function disableDrawingTool() {
      drawingTool = null;
      activeTool = null;
      activeGra = null;
      sketchVM.cancel();
      //setHelpMessage();
   }

   //Set the help message.
   function setHelpMessage() {

      if (drawingTool === "polyline") {
         helpMsg = 'Draw a line on the map to measure distance';
         if (freehand) {
            helpMsg = 'Click or drag on the map to measure distance';
         }

         if (drawingState === "start" || drawingState === "active") {
            helpMsg = 'Click to continue or "Done"(double-click) to finish';
            if (freehand) {
               helpMsg = 'Click or drag to continue. "Done"(double-click) to finish.';
            }
         }

      } else if (drawingTool === "polygon") {
         helpMsg = 'Draw a shape on the map to measure area';
         if (freehand) {
            helpMsg = 'Click or drag on the map to measure area';
         }

         if (drawingState === "start" || drawingState === "active") {
            helpMsg = 'Click to continue or "Done"(double-click) to finish';
            if (freehand) {
               helpMsg = 'Click or drag to continue. "Done"(double-click) to finish.';
            }
         }

      } else if (drawingTool === "rectangle") {
         helpMsg = 'Click on the map to set upper-left of rectangle';
         if (freehand) {
            helpMsg = 'Drag and release on the map to draw a rectangle';
         }

         if (drawingState === "start" || drawingState === "active") {
            helpMsg = 'Click to set lower-right of rectangle';
            if (freehand) {
               helpMsg = 'Drag and release on the map to draw a rectangle';
            }
         }

      } else if (drawingTool === "circle") {
         helpMsg = 'Click on the map to set center of circle';
         if (freehand) {
            helpMsg = 'Drag and release on the map to draw a circle';
         }

         if (drawingState === "start" || drawingState === "active") {
            helpMsg = 'Click to set radius of circle';
            if (freehand) {
               helpMsg = 'Drag and release on the map to draw a circle';
            }
         }

         //Update selected measurement graphic.
      } else if (updateState === "start" || updateState === "active") {
         helpMsg = 'Drag an anchor point to edit the measurement';

      } else { //No active drawing tool.
         helpMsg = 'Choose a measuring tool';

         if (GraLay.measure.graphics.length > 0) {
            helpMsg = 'Choose a tool or select a measurement to edit';
         }
      }
   }

   //Freehand drawing mode changed.
   function drawingModeChanged(event: React.ChangeEvent<HTMLInputElement>) {
      freehand = event.target.checked;

      drawingMode = "click";
      if (freehand) {
         drawingMode = "hybrid";
      }

      sketchVM.defaultCreateOptions.mode = drawingMode;

      if (drawingTool && (drawingState === "cancel" || drawingState === "complete" || !drawingState)) {
         sketchVM.create(drawingTool); // Activate previous drawing tool.
      }
      setHelpMessage();
      refresh();
   }

   //Label all measurements mode changed.
   function labelMeasurementsChanged(event: React.ChangeEvent<HTMLInputElement>) {
      labelMeasurements = event.target.checked;
      refresh();
   }

   //Unit of measure changed.
   function unitChanged(unitType: string, event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) {
      const unit: string = event.target.value;

      if (unitType === "Length") {
         distUnitValue = unit;
      } else {
         areaUnitValue = unit;
      }

      if (activeGra) {
         computeMeasurement();
      }
      refresh();
   }

   //Compute results.
   function computeMeasurement() {
      const polyline = activeGra.geometry as __esri.Polyline;
      const polygon = activeGra.geometry as __esri.Polygon;
      const circle = activeGra.geometry as __esri.Circle;

      computeUnitsOfMeasure(activeGra.geometry);

      //Set circle radius begin and end points.
      if (activeTool === "circle" && (updateTool === "reshape" || updateTool === "transform")) {
         const yMid: number = (circle.extent.ymin + circle.extent.ymax) / 2;

         prevPnt = new Point({ x: circle.centroid.x, y: circle.centroid.y, spatialReference: MainMap.activeView.spatialReference });
         curPnt = new Point({ x: circle.extent.xmax, y: yMid, spatialReference: MainMap.activeView.spatialReference });
      }

      //Compute segment and total distance.
      let segment: number = 0;
      if (view2D) {
         segment = computeSegmentDistance(prevPnt, curPnt);
         bearing = Util_App.getBearing(prevPnt, curPnt);
      }

      if (activeTool === "polyline") {
         if (MainMap.activeView.spatialReference.isWebMercator) {
            distance = geometryEngine.geodesicLength(polyline, distUnitAPI);

         } else {
            distance = geometryEngine.planarLength(polyline, distUnitAPI);
         }

      } else { //Compute area and perimeter values.
         if (MainMap.activeView.spatialReference.isWebMercator) {
            area = geometryEngine.geodesicArea(polygon, areaUnitAPI);
            perimeter = geometryEngine.geodesicLength(polygon, distUnitAPI);

         } else {
            area = geometryEngine.planarArea(polygon, areaUnitAPI);
            perimeter = geometryEngine.planarLength(polygon, distUnitAPI);
         }
      }

      if (distUnitAPI === "feet") {
         distance = Util.round(distance, 1);
         perimeter = Util.round(perimeter, 1);

      } else {
         distance = Util.round(distance, 2);
         perimeter = Util.round(perimeter, 2);
      }

      if (areaUnitAPI === "square-feet") {
         area = Util.round(area, 1);
      } else {
         area = Util.round(area, 2);
      }

      if (view2D) {
         segmentText = Util.formatTextValue(segment, "Comma", true);
      }

      distanceText = Util.formatTextValue(distance, "Comma", true);
      perimeterText = Util.formatTextValue(perimeter, "Comma", true);
      areaText = Util.formatTextValue(area, "Comma", true);
   }

   //Compute segment distance.
   function computeSegmentDistance(pnt1: __esri.Point, pnt2: __esri.Point): number {
      let segDist: number = 0;

      //Create two point line.
      const paths = [[[pnt1.x, pnt1.y], [pnt2.x, pnt2.y]]];

      const twoPntLine = new Polyline({
         paths: paths,
         hasZ: false,
         hasM: false,
         spatialReference: MainMap.activeView.spatialReference,
      });

      //Compute distance.
      if (MainMap.activeView.spatialReference.isWebMercator) {
         segDist = geometryEngine.geodesicLength(twoPntLine, distUnitAPI);
      } else {
         segDist = geometryEngine.planarLength(twoPntLine, distUnitAPI);
      }

      if (distUnitAPI === "feet") {
         segDist = Util.round(segDist, 1);
      } else {
         segDist = Util.round(segDist, 2);
      }
      return segDist;
   }

   //Compute units of measure.
   function computeUnitsOfMeasure(geom: __esri.Geometry) {

      distUnitAPI = distUnitValue.toLowerCase().replace(" ", "-") as __esri.LinearUnits;
      areaUnitAPI = areaUnitValue.toLowerCase().replace(" ", "-") as __esri.AreaUnits;

      distUnitText = distUnitValue.toLowerCase();
      areaUnitText = areaUnitValue.toLowerCase();

      //Compute units for "Auto" distance.
      if (distUnitValue === "Auto") {
         distUnitAPI = "feet";
         distUnitText = distUnitAPI;

         let distMiles: number;

         if (geom.type === "polyline") {
            distMiles = geometryEngine.planarLength(geom, "miles");

         } else { //Polygon.
            const paths = [[[geom.extent.xmin, geom.extent.ymin], [geom.extent.xmax, geom.extent.ymax]]];

            const twoPntLine = new Polyline({
               paths: paths,
               hasZ: false,
               hasM: false,
               spatialReference: MainMap.activeView.spatialReference,
            });

            distMiles = geometryEngine.planarLength(twoPntLine, "miles");
         }

         if (distMiles >= 1) {
            distUnitAPI = "miles";
            distUnitText = distUnitAPI;
         }
      }

      //Compute units for "Auto" area.
      if (areaUnitValue === "Auto") {
         areaUnitAPI = "square-feet";
         areaUnitText = "square feet";

         if (geom.type === "polygon") {
            const polygon = geom as __esri.Polygon;

            const areaMiles: number = geometryEngine.planarArea(polygon, "square-miles");
            const areaAcres: number = geometryEngine.planarArea(polygon, "acres");

            if (areaMiles >= 1) {
               areaUnitAPI = "square-miles";
               areaUnitText = "square miles";

            } else if (areaAcres >= 0.1) {
               areaUnitAPI = "acres";
               areaUnitText = areaUnitAPI;
            }
         }
      }

      distUnitLabel = distUnitText.replace("feet", "ft").replace("miles", "mi").replace("kilometers", "km").replace("meters", "m");
      areaUnitLabel = areaUnitText.replace("square miles", "mi²").replace("square feet", "ft²").replace("square miles", "mi").replace("square meters", "m²").replace("square kilometers", "km²");
   }

   //Delete individual selected measurement graphics.
   function clearSelected() {

      //Remove segment labels.
      const gras: Graphic_extended[] = [];
      for (const gra_normal of GraLay.measureLabels.graphics) {
         const gra = gra_normal as Graphic_extended;
         const myId = gra.myId;
         if (myId.StartsWith(activeGra.attributes.myId)) {
            gras.Add(gra);
         }
      }
      GraLay.measureLabels.removeMany(gras);

      //Delete selected graphic if any.
      if (updateState === "start" || updateState === "active") {
         sketchVM.delete();

      } else if (activeTool) {
         sketchVM.cancel();
      }

      setHelpMessage();

      if (drawingTool) {
         //sketchVM.create(drawingTool); //Reset current drawing tool.

      } else {
         hideResultsPanel = true;
         refresh();
      }
   }

   //Clear all measurements.
   function clearAll() {
      if (activeTool) {
         sketchVM.cancel();
      }

      //Remove all graphics.
      GraLay.measure.removeAll();
      GraLay.measureLabels.removeAll();
      setHelpMessage();

      //Restart active tool if any.
      if (drawingTool) {
         sketchVM.create(drawingTool);

      } else {
         hideResultsPanel = true;
         refresh();
      }
   }

   //Create feature layer for labeling.
   function createLabelFeatureLayer() {
      //Line label.
      const geom = new Polyline({
         paths: [[[1, 1], [2, 2]]], hasZ: false, hasM: false,
         spatialReference: MainMap.activeView.spatialReference,
      });

      const attrs: object = { OBJECTID: 1, id: "", drawingTool: "", value: "" };
      lineGra = new Graphic({ geometry: geom, attributes: attrs }) as Graphic_extended;

      const fields: __esri.Field[] = [
         new Field({ name: "OBJECTID", alias: "OBJECTID", type: "oid" }),
         new Field({ name: "id", alias: "id", type: "string" }),
         new Field({ name: "drawingTool", alias: "drawingTool", type: "string" }),
         new Field({ name: "value", alias: "value", type: "string" }),
      ];

      const lineLabelClass = new LabelClass({
         labelExpressionInfo: { expression: "$feature.value" },
         labelPlacement: "center-along",
         //deconflictionStrategy: "none",
         symbol: {
            type: "text",  // autocasts as new TextSymbol()
            color: "black",
            font: {
               size: 10,
               weight: "bold",
            },
            haloColor: "white",
            haloSize: 2,
            verticalAlignment: "middle",
         },
      });

      let lineRenderer = new SimpleRenderer({
         symbol: new SimpleLineSymbol({
            color: new Color([0, 0, 0, 0.01]),
            width: 0.01,
         })
      });

      lineFL = new FeatureLayer({
         id: "MeasureFL",
         title: "MeasureFL",
         geometryType: "polyline",
         objectIdField: "OBJECTID",
         fields: fields,
         outFields: ["*"],
         spatialReference: MainMap.activeView.spatialReference,
         legendEnabled: false,
         listMode: "hide",
         popupEnabled: false,
         renderer: lineRenderer,
         labelingInfo: [lineLabelClass],
         source: [lineGra],
      });

      if (view2D) {
         MainMap.map.add(lineFL);
      }
   }

   //Get buttons.
   function getButtons(): JSX.Element[] {
      const toolNames: string[] = ["Polyline", "Polygon", "Rectangle", "Circle"];

      //Add button to finish off a drawing.
      if (view2D && activeGra) {
         const polyline = activeGra.geometry as __esri.Polyline;
         const polygon = activeGra.geometry as __esri.Polygon;

         if (drawingState === "active" && (drawingTool === "polyline" && polyline.paths[0].length > 2)) {
            toolNames.Add("Done");

         } else if (drawingState === "active" && (drawingTool === "polygon" && polygon.rings[0].length > 4)) {
            toolNames.Add("Done");

         } else {
            toolNames.Add("Clear");
         }
      }

      const jsx: JSX.Element[] = [];

      for (const tool of toolNames) {
         if (!tool.StartsWith(" ")) {
            const ti: Settings.iToolInfo = Settings.getToolInfo(tool);

            let backColor: ButtonProps["color"] = "primary";
            if (drawingTool === tool.toLowerCase()) {
               backColor = "secondary";
            }

            let disabled: boolean = false;
            if (tool === "Clear" && (updateState !== "start" && updateState !== "active")) {
               disabled = true;
            }
            if (tool === "Clear" && drawingState === "active") {
               disabled = false;
            }

            let ariaLabel = tool;
            if (tool === "Polyline") {
               ariaLabel = "Distance";
            } else if (tool === "Polygon") {
               ariaLabel = "Area";
            }

            jsx.push(
               <Button key={Util.uid()} disabled={disabled} color={backColor} onClick={() => drawingToolChanged(tool.toLowerCase())} title={ti.tip} aria-label={ariaLabel}>
                  <Icon style={{ fontSize: 17, color: ti.iconColorToolbar }}>{ti.iconName}</Icon>
                  <span style={{ marginLeft: 4 }}></span>
                  {ti.label}
               </Button>,
            );
         }
      }
      return jsx;
   }

   //Set results visibility.
   //Util.errMsg("ActTool DraSta UpSta: " + activeTool + ", " + drawingState + ", " + updateState);
   let resultsVisible: boolean = true;
   if (hideResultsPanel) {
      resultsVisible = false;
      hideResultsPanel = false;
   }

   if (drawingState !== "complete") {
      segmentVisible = false;
      bearingVisible = false;
      distanceVisible = false;
      radiusVisible = false;
      perimeterVisible = false;
      areaVisible = false;

      if (activeTool === "polyline" || activeTool === "polygon") {
         if (view2D) {
            segmentVisible = true;
            bearingVisible = true;
         }

         if (activeTool === "polyline") {
            distanceVisible = true;
         }
      }
      if (activeTool === "circle") {
         radiusVisible = true;
      }
      if (activeTool !== "polyline") {
         perimeterVisible = true;
         areaVisible = true;
      }

      if (updateState === "start" || updateState === "active") {
         segmentVisible = false;
         bearingVisible = false;
      }
   }

   let clearAllDisabled: boolean = false;
   if (GraLay.measure.graphics.length === 0) {
      clearAllDisabled = true;
   }

   //Styles.
   const styles: Default.iStyles = {
      toolStyle: { width: Settings.leftPanelWidth, maxWidth: Settings.leftPanelWidth, padding: 5, paddingTop: 0, paddingBottom: 3, overflow: 'auto' },
      help: { marginTop: 15, padding: 5, backgroundColor: yellow[200] },
      col1Container: {
         display: 'flex', flexDirection: 'column', justifyContent: 'space-around', alignItems: 'flex-start',
         backgroundColor: blueGrey[50], marginTop: 9, padding: 7, paddingTop: 4, borderRadius: 4,
      },
   };

   return (
      <Paper>
         <WidgetHeading.Render title={toolName} onClose={close} />

         <div style={styles.toolStyle}>
            <ButtonGroup variant="contained" fullWidth aria-label="Measure toolbar buttons">
               {getButtons()}
            </ButtonGroup>

            <Typography variant="body2" style={styles.help}>
               {helpMsg}
            </Typography>

            <div className="app-flex-row-start" style={{ marginTop: 9, marginLeft: 5 }}>
               <FormControlLabel title="Enable freehand drawing"
                  control={<Checkbox inputProps={{ id: "enable-freehand-measure" }} checked={freehand} color="primary" onChange={drawingModeChanged} />}
                  label="Enable freehand drawing"
               />
               <Button variant="outlined" size="medium" color="info" onClick={clearAll} disabled={clearAllDisabled} title="Delete all measurements" aria-label="Clear all">
                  Clear All
               </Button>
            </div>

            <div className="app-flex-row-start" style={{ marginTop: 2, marginLeft: 5 }}>
               <FormControlLabel title="Label all measurements"
                  control={<Checkbox inputProps={{ id: "label-all-measure" }} checked={labelMeasurements} color="primary" onChange={labelMeasurementsChanged} />}
                  label="Label all measurements"
               />
            </div>

            <div className="app-flex-row-between" style={{ marginTop: 9 }}>

               <TextField select style={{ width: 150 }}
                  label="Length Units"
                  title="Set length unit of measurement"
                  inputProps={{ id: "length-units" }}
                  InputLabelProps={{ htmlFor: "length-units" }}
                  value={distUnitValue}
                  onChange={(event) => unitChanged("Length", event)}
               >
                  {Util_MUI.buildSelectMenuItems(distUnitNames)}
               </TextField>

               <TextField select style={{ width: 150 }}
                  label="Area Units"
                  title="Set area unit of measurement"
                  inputProps={{ id: "area-units" }}
                  InputLabelProps={{ htmlFor: "area-units" }}
                  value={areaUnitValue}
                  onChange={(event) => unitChanged("Area", event)}
               >
                  {Util_MUI.buildSelectMenuItems(areaUnitNames)}
               </TextField>
            </div>

            {resultsVisible && <div style={styles.col1Container}>
               {segmentVisible && <Typography variant="body2">Segment: {segmentText} {distUnitText}</Typography>}
               {bearingVisible && <Typography variant="body2">Bearing: {bearing}</Typography>}
               {distanceVisible && <Typography variant="body2">Distance: <b>{distanceText}</b> {distUnitText}</Typography>}
               {radiusVisible && <Typography variant="body2">Radius: <b>{segmentText}</b> {distUnitText}</Typography>}
               {perimeterVisible && <Typography variant="body2">Perimeter: {perimeterText} {distUnitText}</Typography>}
               {areaVisible && <Typography variant="body2">Area: <b>{areaText}</b> {areaUnitText}</Typography>}
            </div>}
         </div>

      </Paper>
   ); //end return.

} //end function.
